<template>
  <app-full-wrapper
    @click="close(MODAL_CLOSE_BY_WRAPPER)"
    @opened="$emit('opened', this)"
    @closed="$emit('closed', this)"
    ref="wrapper"
    class="app-modal-wrapper"
    :class="{
      bottom: position === 'bottom',
    }"
    :destroyOnClose="$attrs.destroyOnClose"
    :blur="$attrs.blur"
  >
    <div class="app-modal" :class="{ 'no-padding': noPadding }">
      <slot />
    </div>
  </app-full-wrapper>
</template>

<script>
const STATE_CLOSED = 0
const STATE_CLOSING = 1
const STATE_OPENING = 2
const STATE_OPENED = 3

import {
  MODAL_CLOSE_BY_CALL,
  MODAL_CLOSE_BY_BACK_BUTTON,
  MODAL_CLOSE_BY_WRAPPER,
} from '@/constants'

export default {
  name: 'AppModal',

  data() {
    return {
      MODAL_CLOSE_BY_WRAPPER,

      showWrapper: false,
      state: STATE_CLOSED,
      beforeEachFn: undefined,
      scrollY: undefined,
    }
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },

    noPadding: {
      type: Boolean,
      default: false,
    },

    closeOnWrapperClick: {
      type: Boolean,
      default: true,
    },

    closeOnBack: {
      type: Boolean,
      default: true,
    },

    goBackOnClosed: {
      type: Boolean,
      default: false,
    },

    position: {
      type: String,
    },
  },

  computed: {
    isOpen() {
      return [STATE_OPENING, STATE_OPENED].includes(this.state)
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(active) {
        switch (active) {
          case true:
            this.open()
            break

          case false:
            this.close(MODAL_CLOSE_BY_CALL)
            break
        }
      },
    },
  },

  destroyed() {
    this.removeBeforeEachHook()
  },

  beforeCreate() {
    this.$on('opening', () => {
      this.scrollY = window.scrollY
      this.addBeforeEachHook()
      this.$refs.wrapper.open()
      this.$emit('input', true)
    })
      .$on('closing', () => {
        this.removeBeforeEachHook()
        this.$refs.wrapper.close()
      })
      .$on('closed', () => {
        if (this.scrollY !== undefined) {
          window.scrollTo(0, this.scrollY)
          this.scrollY = undefined
        }

        this.$emit('input', false)
      })
  },

  mounted() {
    const wrapper = this.$el

    wrapper.addEventListener('transitionend', event => {
      if (event.propertyName !== 'opacity') return

      switch (this.state) {
        case STATE_OPENED:
        case STATE_OPENING:
          this.state = STATE_OPENED
          this.$emit('opened', this)
          break

        case STATE_CLOSED:
        case STATE_CLOSING:
          this.state = STATE_CLOSED
          this.$emit('closed', this)
          break
      }
    })
  },

  methods: {
    async addBeforeEachHook() {
      await this.removeBeforeEachHook()

      this.beforeEachFn = app.$router.beforeEach(async (to, from, next) => {
        if (app.isGoToScreen === true) return next()

        const currentWrapperId = app.getCurrentFullWrapper()
        const componentWrapperId = this.$refs.wrapper?._uid

        if (currentWrapperId === componentWrapperId && this.isOpen === true) {
          if (this.closeOnBack === true) {
            const closeArgs = MODAL_CLOSE_BY_BACK_BUTTON

            if (typeof this.$listeners.beforeBack === 'function') {
              const result = await this.$listeners.beforeBack(this)

              if (result !== false) this.close(closeArgs)
            } else {
              this.close(closeArgs)
            }
          }

          if (!this.goBackOnClosed && !app.isGoToScreen) {
            next(false)
            return
          }
        }

        next()
      })
    },

    async removeBeforeEachHook() {
      if (typeof this.beforeEachFn === 'function') await this.beforeEachFn()

      this.beforeEachFn = undefined
    },

    async open() {
      return new Promise(resolve => {
        if ([STATE_OPENING, STATE_OPENED].includes(this.state)) return resolve()

        this.$once('opened', resolve)

        this.state = STATE_OPENING

        this.$emit('opening', this)
      })
    },

    async close(closeBy = MODAL_CLOSE_BY_CALL) {
      return new Promise((resolve, reject) => {
        if ([STATE_CLOSING, STATE_CLOSED].includes(this.state)) return resolve()

        if (
          closeBy === MODAL_CLOSE_BY_WRAPPER &&
          this.closeOnWrapperClick === false
        )
          return reject()

        const closeArgs = { closeBy, modal: this }

        if (
          !app.isGoToScreen &&
          closeBy !== MODAL_CLOSE_BY_BACK_BUTTON &&
          this.goBackOnClosed === true
        )
          this.$once('closing', app.goBackScreen)

        this.$once('closed', () => resolve(closeArgs))

        this.state = STATE_CLOSING

        this.$emit('closing', closeArgs)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/settings.scss';
// @import '@/styles/functions.scss';
$transitionDuration: 400ms;
$padding: 0.5rem;

.app-modal-wrapper {
  padding: $padding;
  display: flex;
  flex-direction: column;
  z-index: 11;

  &.bottom {
    justify-content: flex-end;
    padding: 1rem 0 0;

    > .app-modal {
      margin: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      max-width: 100vw;
      min-width: 100vw;
      max-height: stretch;
      transform: translateY(100%);
    }

    &.opened,
    &.opening {
      > .app-modal {
        transform: translateY(0%);
      }
    }

    :deep(.app-layout-footer) {
      .app-card {
        &:last-child {
          padding-top: 0;
          border-radius: 0;
        }
      }
    }
  }

  &.opened,
  &.opening {
    > .app-modal {
      opacity: 1;
    }
  }
}

.app-modal {
  max-width: stretch;
  min-width: 288px;
  margin: auto;
  transition: opacity $transitionDuration, transform $transitionDuration;
  opacity: 0;
  background-color: #fff;
  // min-width: stretch;
  // max-height: calc(var(--viewport-height) - #{$padding * 2});
  border-radius: $defaultBorderRadius;
  padding: $defaultPadding;
  // flex: 0;
  // display: flex;
  // flex-direction: column;

  :deep(> .app-layout) {
    overflow-x: hidden;
    min-height: 0;
    height: auto;

    > .app-layout-header {
      > .app-card {
        &:first-child {
          border-top-left-radius: $defaultBorderRadius;
          border-top-right-radius: $defaultBorderRadius;
        }
      }
    }
  }

  // :deep(.app-layout-header) {
  //   &.fixed {
  //     left: $padding;
  //     right: #{$padding * 2};
  //   }
  // }

  :deep(.app-layout-footer) {
    &.fixed {
      width: calc(100% - #{$padding * 2});
      bottom: unset;
    }
  }

  :deep(.app-layout-content) {
    > .app-card {
      &.no-padding:only-child {
        overflow-y: auto;
      }
    }
  }
}
</style>
